import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { useQuery } from 'react-query'

// Libs
import { fetchProductRecommend } from '@/api/fetchProductRecommend'
import { useAppSelector } from '@/redux/hooks'
import useMedia from '@/hooks/useMedia'
import { useObserver } from '@/hooks/useObserver'
import { handleGtmViewItemList } from '@/methods/handleGtmEvent'
import { getVisitorId, REC_SOURCE } from '@/methods/recommendTracking'

// Components
import ProductCard from '@/components/shared/ProductCard'
import HotSectionTitle from '../utils/HotSectionTitle'

// Constants
import { GtmItemListName } from '@/constants/gtmEvent'
import { QUERY_KEY } from '@/constants/queryKey'

// Styles
import styles from './style.module.scss'

const cx = classNames.bind(styles)

// 一排：桌機 6 欄、平板 3 欄；手機 2 欄兩排
const DESKTOP_LIMIT = 6
const TABLET_LIMIT = 3
const MOBILE_LIMIT = 4
// 只看過一個商品時不顯示（就是剛剛那個）
const MIN_ITEMS = 2

const GTM_LIST = {
  itemListName: GtmItemListName.RECOMMEND_RECENTLY_VIEWED,
  itemListId: `recommend_${REC_SOURCE.RECENTLY_VIEWED}`,
  useListForItems: true,
}

// 最近看過：會員是登入時看的加上這台裝置登入前看的，訪客是這台裝置看的；只列目前買得到的商品
const RecentlyViewedProducts = () => {
  const { t } = useTranslation()
  const { isMobile, isTablet } = useMedia()
  const memberInfoId = useAppSelector((state) => state.member?.memberData?.memberInfoId)
  const limit = (() => {
    if (isMobile) return MOBILE_LIMIT
    if (isTablet) return TABLET_LIMIT
    return DESKTOP_LIMIT
  })()
  // 只在瀏覽器取得（SSR 沒有 localStorage）
  const [visitorId, setVisitorId] = useState('')
  useEffect(() => setVisitorId(getVisitorId()), [])

  const { data } = useQuery(
    [QUERY_KEY.FETCH_PRODUCT_RECOMMEND, { type: 'recently-viewed', memberInfoId, visitorId, limit }],
    () => fetchProductRecommend({ type: 'recently-viewed', visitorId, limit }),
    { enabled: !!memberInfoId || !!visitorId, staleTime: 60 * 1000 },
  )
  const items = (data?.items?.length ?? 0) >= MIN_ITEMS ? data!.items : []

  // 區塊第一次出現在畫面上時送一次 view_item_list
  const sentRef = useRef(false)
  const { observerRef } = useObserver({
    observerCallback: (entries) => {
      if (sentRef.current || !items.length || !entries.some((entry) => entry.isIntersecting)) return
      sentRef.current = true
      handleGtmViewItemList({ ...GTM_LIST, productInfoList: items })
    },
    dep: [items],
  })

  if (!items.length) return null

  return (
    <div className={cx('recently-viewed-products')}>
      <HotSectionTitle>{t('Home_Recently_Viewed_Title')}</HotSectionTitle>
      <div className={cx('recently-viewed-products__list')}>
        {items.map((item) => (
          <ProductCard
            key={item.productId}
            {...(item as any)}
            observer={observerRef.current}
            gtmItemEventParams={GTM_LIST}
            recSource={REC_SOURCE.RECENTLY_VIEWED}
          />
        ))}
      </div>
    </div>
  )
}

export default RecentlyViewedProducts
